import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Collapse,
	Divider,
	Grid,
	Radio,
	Stack,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import { formatAge } from '@/utils/ageHelper';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import dayjs from 'dayjs';
import {
	useDeleteWellBeingScaleMutation,
	useGetWellBeingScaleDetailQuery,
} from '@/api/forms-management/wellBeingScale';
import LogHistory from '../LogHistory';
import FormManagementChip from '@/common/components/FormManagementChip';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { showLabel } from '@/utils/helper';
import { ZH_YESNO } from '@/common/constants/common';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
	useGetExportCustomerPDFMutation,
	useGetWellBeingScaleGenerateReportMutation,
} from '@/api/common';

export interface DataGrid {
	updatedAt: string;
	createdAt: string;
}
export default function WellBeingScaleDetailPage() {
	const { t } = useTranslation();
	const { preAdmissionId, wellBeingScaleId, admissionRequestId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const memberListType = location.pathname.includes('admission-request-list')
		? 'admission-request-list'
		: 'member-list';
	const [deleteWellBeingScale] = useDeleteWellBeingScaleMutation();
	const showModal = useModal();

	const [getExportCustomerPDF] = useGetExportCustomerPDFMutation();
	const [getWellBeingScaleGenerateReport] =
		useGetWellBeingScaleGenerateReportMutation();

	const [tableExpanded, setTableExpanded] = useState(true);

	const [answerList, setAnswerList] = useState(() =>
		Array.from({ length: 20 }, (_, i) => {
			const id = i + 1;
			return {
				id,
				title: t(`preAdmission.title${id}`),
				answer1: false,
				answer2: false,
				answer3: false,
				answer4: false,
				answer5: false,
			};
		})
	);

	const { data: details }: any = useGetWellBeingScaleDetailQuery(
		wellBeingScaleId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	useEffect(() => {
		if (details) {
			const restoredList = details.data.answerList.map(
				(score: number, index: number) => {
					const id = index + 1;
					const title = t(`preAdmission.title${id}`);
					const row: any = {
						id,
						title,
						answer1: false,
						answer2: false,
						answer3: false,
						answer4: false,
						answer5: false,
					};

					row[`answer${score}`] = true;
					return row;
				}
			);
			setAnswerList(restoredList);
		}
	}, [details]);

	const columns: GridColDef[] = [
		{
			field: 'title',
			headerName: '',
			flex: 30,
		},
		{
			field: 'answer1',
			headerName: t('preAdmission.score1'),
			flex: 10,
			renderCell: (params: any) => radioRenderCell(params, 1),
		},
		{
			field: 'answer2',
			headerName: t('preAdmission.score2'),
			flex: 10,
			renderCell: (params: any) => radioRenderCell(params, 2),
		},
		{
			field: 'answer3',
			headerName: t('preAdmission.score3'),
			flex: 10,
			renderCell: (params: any) => radioRenderCell(params, 3),
		},
		{
			field: 'answer4',
			headerName: t('preAdmission.score4'),
			flex: 10,
			renderCell: (params: any) => radioRenderCell(params, 4),
		},
		{
			field: 'answer5',
			headerName: t('preAdmission.score5'),
			flex: 10,
			renderCell: (params: any) => radioRenderCell(params, 5),
		},
	];

	const radioRenderCell = (params: any, score: number) => {
		const { id, row, api } = params;

		const isChecked = row[`answer${score}`] === true;
		return <Radio checked={isChecked} value={score} disabled={true} />;
	};

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={false}
				title={t('preAdmission.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>
						{t('preAdmission.isDeleteForm', {
							formName: t('preAdmission.wellBeingScale'),
						})}
					</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteWellBeingScale(details?.data.id || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.enquiryRecordDeleteSuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});
						navigate(
							preAdmissionId
								? `/pre-admission/pre-admission-list/detail/${preAdmissionId}/forms-management`
								: `/member-management/${memberListType}/detail/${admissionRequestId}/forms-management`,
							{ replace: true }
						);
					}
				}}
			/>
		));
	};

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.wellBeingScaleDetail')}
						<FormManagementChip
							sx={{ marginLeft: '10px' }}
							status={details?.data.formStatus}
							label={details?.data.formStatusDesc}
						/>
					</Typography>
					<Box>
						<Button
							endIcon={<FileDownloadOutlinedIcon />}
							onClick={async () => {
								const response = await getWellBeingScaleGenerateReport(
									details?.data.id
								);
								if ('data' in response) {
									getExportCustomerPDF(response?.data?.data.url);
								}
							}}
							variant='contained'
							sx={{ marginRight: '24px' }}
						>
							{t('button.exportAsPdf')}
						</Button>
						<Button
							endIcon={<DeleteOutlinedIcon />}
							onClick={() => handleDelete()}
							variant='contained'
						>
							{t('button.delete')}
						</Button>
					</Box>
				</Box>

				<Card sx={{ padding: '24px 24px', marginTop: 3 }}>
					<Grid container spacing={3} marginTop={0}>
						<Grid size={12}>
							<AppFieldView
								label={t('preAdmission.fillingDate')}
								value={
									details?.data.fillingDate
										? dayjs(details?.data.fillingDate)
												.locale('en')
												.format(DISPLAY_DATE_FORMAT)
										: ''
								}
							/>
						</Grid>

						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.userName')}
								value={details?.data.nameZh}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.idCardNo')}
								value={details?.data.hkid}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.zh_fileNo')}
								value={details?.data.fileNo}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.zh_unit')}
								value={details?.data.branchName}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.birthday')}
								value={
									details?.data.dateOfBirth
										? dayjs(details?.data.dateOfBirth)
												.locale('en')
												.format(DISPLAY_DATE_FORMAT)
										: ''
								}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.zh_age')}
								value={formatAge(
									details?.data.currentAge,
									details?.data.currentAgeEstimated,
									'zh'
								)}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.zh_sex')}
								value={details?.data.sexDesc}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.serviceUsageTime')}
								value={details?.data.serviceDate}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.zh_diagnose')}
								value={details?.data.diagnosisDesc}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.zh_diagnose_other')}
								value={details?.data.diagnosisSpecify}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.zh_training')}
								value={details?.data.vocationalTrainingDesc}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.zh_training_other')}
								value={details?.data.vocationalTrainingSpecify}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.degreeEducation')}
								value={details?.data.educationDesc}
							/>
						</Grid>
						<Grid size={6}>
							<AppFieldView
								label={t('preAdmission.degreeOfFamilySupport')}
								value={details?.data.degreeOfFamilySupportDesc}
							/>
						</Grid>
					</Grid>

					<Divider sx={{ margin: '24px 0' }} />
					<Box
						display='flex'
						justifyContent='space-between'
						onClick={() => setTableExpanded(!tableExpanded)}
					>
						<Typography
							variant='h6'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('preAdmission.questionnaire')}
						</Typography>
						<ExpandMoreIcon
							sx={{
								transform: tableExpanded ? 'rotate(180deg)' : 'none',
							}}
						/>
					</Box>

					<Collapse in={tableExpanded}>
						<Typography
							variant='body2'
							sx={{ fontWeight: 'bold' }}
							marginBottom={2}
						>
							{t('preAdmission.wbsTips')}
						</Typography>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12} display='flex'>
								<LocalDataGrid
									rows={answerList}
									columns={columns}
									getRowId={(row) => row.id}
									pagination={false}
								/>
							</Grid>
							<Typography
								variant='body1'
								sx={{
									borderBottom: '1px solid #e0e0e0',
									width: '100%',
									textAlign: 'right',
									position: 'relative',
									top: '-10px',
									paddingBottom: '10px',
								}}
								marginBottom={2}
							>
								{t('preAdmission.totalScore')}
								<span
									style={{
										padding: '0 0 0 50px',
										display: 'inline-block',
										marginRight: '10.5%',
									}}
								>
									{details?.data.totalScore}
								</span>
							</Typography>
						</Grid>
					</Collapse>
				</Card>

				{details?.data.id && (
					<Card sx={{ margin: '24px 0' }}>
						<CardContent>
							<LogHistory formId={details.data.id} />
						</CardContent>
					</Card>
				)}
			</Box>
		</>
	);
}
